import { useEffect, useState } from 'react';
import { Printer, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Row = {
  id: number; date: string; type: string; direction: string; reference: string; notes?: string; status: string;
  shipmentId?: number; trackingNumber?: string; amount: number; fee: number; net: number; balance: number;
};
type Statement = {
  customer: { id: number; name: string; phone?: string };
  openingBalance: number; totalCredit: number; totalDebit: number; closingBalance: number; truncated: boolean; rows: Row[];
};
type CustomerHit = { id: number; name: string; phone?: string };

const money = (n: number) => new Intl.NumberFormat('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const typeLabel: Record<string, string> = { CodCollected: 'تحصيل COD', DelegateCommission: 'عمولة مندوب', Settlement: 'تسوية', ShippingFee: 'رسوم شحن' };

export default function CustomerStatementPage() {
  const { user } = useAuth() as any;
  const roles: string[] = (user?.roles || (user?.role ? [user.role] : [])).map((r: string) => String(r).toLowerCase());
  const isMerchantOnly = roles.includes('merchant') && !roles.some(r => ['admin', 'ceo', 'owner'].includes(r));

  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<CustomerHit[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const load = async (id?: number | null) => {
    setLoading(true); setMessage('');
    try {
      const params = new URLSearchParams();
      const cid = id ?? customerId;
      if (cid) params.set('customerId', String(cid));
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const res = await api.get(`/api/accounting/statement?${params.toString()}`);
      setData(res.data?.data || null);
    } catch (e: any) {
      setData(null);
      setMessage(e?.response?.data?.message || 'تعذر تحميل كشف الحساب.');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isMerchantOnly) void load(); /* eslint-disable-next-line */ }, [isMerchantOnly]);

  const doSearch = async () => {
    try {
      const res = await api.get(`/api/accounting/statement/customers?q=${encodeURIComponent(search)}`);
      setHits(res.data?.data || []);
    } catch { setMessage('تعذر البحث عن العملاء.'); }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <h1 className="text-2xl font-bold">كشف حساب العميل</h1>
        {data && <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 ml-2" />طباعة</Button>}
      </div>

      <Card className="no-print">
        <CardContent className="pt-6 space-y-4">
          {!isMerchantOnly && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="ابحث باسم العميل أو رقم الهاتف"
                  onKeyDown={e => { if (e.key === 'Enter') void doSearch(); }} />
                <Button variant="outline" onClick={doSearch}><Search className="w-4 h-4" /></Button>
              </div>
              {hits.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {hits.map(h => (
                    <Button key={h.id} size="sm" variant={customerId === h.id ? 'default' : 'outline'}
                      onClick={() => { setCustomerId(h.id); }}>
                      {h.name}{h.phone ? ` — ${h.phone}` : ''}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="text-sm block mb-1">من تاريخ</label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
            <div><label className="text-sm block mb-1">إلى تاريخ</label><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
            <Button disabled={loading || (!isMerchantOnly && !customerId)} onClick={() => load()}>
              {loading ? 'جارٍ التحميل...' : 'عرض الكشف'}
            </Button>
          </div>
          {message && <p className="text-sm text-red-600">{message}</p>}
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardHeader>
            <CardTitle>
              {data.customer.name}{data.customer.phone ? ` — ${data.customer.phone}` : ''}
              <span className="block text-sm font-normal text-gray-500 mt-1">
                {from || to ? `الفترة: ${from || 'البداية'} إلى ${to || 'اليوم'}` : 'كل الفترات'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">رصيد افتتاحي</div><div className="font-bold">{money(data.openingBalance)}</div></div>
              <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">إجمالي المستحق للعميل</div><div className="font-bold text-emerald-700">{money(data.totalCredit)}</div></div>
              <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">إجمالي المخصوم / المدفوع</div><div className="font-bold text-red-700">{money(data.totalDebit)}</div></div>
              <div className="rounded-lg border p-3 bg-gray-50"><div className="text-xs text-gray-500">الرصيد الختامي</div><div className="font-bold">{money(data.closingBalance)}</div></div>
            </div>
            {data.truncated && <p className="text-sm text-amber-700">تم عرض أول 5000 حركة فقط، ضيّق الفترة الزمنية.</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-right">
                    <th className="p-2 border">التاريخ</th><th className="p-2 border">البيان</th><th className="p-2 border">رقم الشحنة</th>
                    <th className="p-2 border">المرجع</th><th className="p-2 border">الحالة</th>
                    <th className="p-2 border">مستحق للعميل</th><th className="p-2 border">مخصوم</th><th className="p-2 border">الرصيد</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50"><td className="p-2 border" colSpan={7}>رصيد افتتاحي</td><td className="p-2 border font-semibold">{money(data.openingBalance)}</td></tr>
                  {data.rows.length === 0 && <tr><td className="p-4 border text-center text-gray-500" colSpan={8}>لا توجد حركات في هذه الفترة</td></tr>}
                  {data.rows.map(r => (
                    <tr key={r.id}>
                      <td className="p-2 border whitespace-nowrap">{new Date(r.date).toLocaleDateString('ar-EG')}</td>
                      <td className="p-2 border">{typeLabel[r.type] || r.type}{r.notes ? <span className="block text-xs text-gray-500">{r.notes}</span> : null}</td>
                      <td className="p-2 border">{r.trackingNumber || '—'}</td>
                      <td className="p-2 border">{r.reference}</td>
                      <td className="p-2 border">{r.status === 'Settled' ? 'مُسوّى' : r.status === 'Pending' ? 'معلّق' : r.status}</td>
                      <td className="p-2 border text-emerald-700">{r.net >= 0 ? money(r.net) : ''}</td>
                      <td className="p-2 border text-red-700">{r.net < 0 ? money(-r.net) : ''}</td>
                      <td className="p-2 border font-semibold">{money(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
